import React, { useContext, useEffect, useState } from "react";
import {
  Banner,
  LegacyCard as Card,
  FormLayout,
  Layout,
  PageActions,
  BlockStack,
  InlineStack,
  Button,
  ChoiceList,
} from "@shopify/polaris";
import { useMutation } from "@apollo/client";

// import hoc
import { baseHelper, errorHelper } from "lib/helpers";
import { withErrorBoundary } from "lib/hoc";

// import constant
import constant from "lib/constant/constant";

// import context
import { PrivateContext } from "lib/context/privateContext";

// import components
import FileUpload from "app/product/modules/operator/features/editCsv/fileUpload";
import { Sheet, SkeletonAnnotated } from "lib/components";

// import mutation
import { BULK_EDIT_UPLOAD, BULK_PARTIAL_EDIT_UPLOAD } from "app/product/apollo/mutations";
import { useNavigate } from "react-router-dom";

// Edit mode constants
const EDIT_MODE_ALL = "all";
const EDIT_MODE_PARTIAL = "partial";

const OperatorProductEditCsv = () => {
  const { cms, isLoading } = useContext(PrivateContext);
  const navigate = useNavigate();
  const { gql, CRITICAL, SUCCESS, INFO } = constant;
  const [banner, setBanner] = useState({
    action: null,
    children: null,
    isOpen: false,
    status: "",
    title: "",
  });

  const [values, setValues] = useState({});
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [editMode, setEditMode] = useState([EDIT_MODE_ALL]);
  const [bulkProductUploadRequest, { loading: bulkUploadLoading }] = useMutation(BULK_EDIT_UPLOAD);
  const [bulkPartialProductUploadRequest, { loading: bulkPartialUploadLoading }] = useMutation(BULK_PARTIAL_EDIT_UPLOAD);
  const [sheetActive, setSheetActive] = useState(false);

  const isPartialEdit = editMode[0] === EDIT_MODE_PARTIAL;
  const isAnyLoading = bulkUploadLoading || bulkPartialUploadLoading;

  const getCSVData = (csvValue) => {
    const regexForEnterKey = /\n,|\n/gi;
    const csvDataAfterRegex = csvValue.data.replace(regexForEnterKey, "\n");
    return csvDataAfterRegex;
  };

  const onSubmit = async () => {
    if (!values.data) {
      setBanner({
        isOpen: true,
        title: cms("common.message.error.csvIsRequired"),
        status: CRITICAL,
      });
      return;
    }
    const modifiedFormat = getCSVData(values);
    const requestData = {
      file: {
        file: modifiedFormat,
        fileName: values.fileName,
      },
      isBulkEdit: true,
    };
    try {
      // Use partial edit mutation if partial mode is selected
      const mutationFn = isPartialEdit ? bulkPartialProductUploadRequest : bulkProductUploadRequest;
      const gqlKey = isPartialEdit ? gql.BULK_PARTIAL_EDIT_PRODUCT_CSV : gql.BULK_EDIT_PRODUCT_CSV;
      
      const res = await mutationFn({
        variables: {
          input: requestData,
        },
      });

      const resData = baseHelper.getResponseData(res.data, gqlKey);
      // eslint-disable-next-line no-shadow
      let banner = {
        isOpen: true,
        status: INFO,
        title: cms("section.bulk.message.success.title"),
        children: cms("section.bulk.message.success.content"),
        action: {
          content: cms("section.bulk.message.success.action"),
          onAction: () => navigate("/activity/bulk-edit"),
        },
      };

      if (!resData) {
        const error = baseHelper.getResponseError(res.data, gqlKey);
        banner = {
          action: null,
          isOpen: true,
          status: CRITICAL,
          title: error,
        };
      }
      setIsSubmitDisabled(true);
      setBanner(banner);
    } catch (exception) {
      setBanner({
        isOpen: true,
        status: CRITICAL,
        title: errorHelper.parse(exception),
      });
    }
  };

  const primaryAction = {
    content: cms("common.label.done"),
    onAction: () => setSheetActive(false),
  };

  const secondaryAction = {
    content: cms("common.button.cancel"),
    onAction: () => setSheetActive(false),
  };

  useEffect(() => {
    if (values.data) {
      setIsSubmitDisabled(false);
    }
    if (!values.data) {
      setIsSubmitDisabled(true);
    }
  }, [values]);

  const onDismissBanner = () => {
    setBanner({
      action: null,
      children: null,
      isOpen: false,
      status: "",
      title: "",
    });
  };

  if (isLoading) {
    return <SkeletonAnnotated />;
  }

  return (
    <>
      {banner.isOpen && (
        <Layout.Section>
          <Banner
            title={banner.title}
            status={banner.status}
            isOpen={banner.isOpen}
            action={banner.action}
            onDismiss={onDismissBanner}
          >
            {banner.children}
          </Banner>
        </Layout.Section>
      )}
      <br />
      <Layout.AnnotatedSection title={cms("section.setting.title")} description={cms("section.setting.description")}>
        <Card title={cms("section.bulk.title")}>
          <Card.Section>
            <BlockStack gap={"400"}>
              {cms("section.bulk.description")}
              <br />
              <Banner status="info">
                <p>{cms("section.bulk.label.exportProduct")}</p>
              </Banner>
            </BlockStack>
            <br />
            <FormLayout>
              <ChoiceList
                title="Select Edit Mode"
                choices={[
                  {
                    label: "Bulk Edit All Fields (variants can be added/deleted)",
                    value: EDIT_MODE_ALL,
                    helpText: "All columns must be present in CSV. Variants can be added or removed.",
                  },
                  {
                    label: "Bulk Edit Partial Fields (only update existing variants)",
                    value: EDIT_MODE_PARTIAL,
                    helpText: "Handle, Title, Vendor, and all variant option headers (Option 1-3 Name/Value) are required. Other columns are optional - only present columns will be updated. Existing variants can only be updated, not added or deleted.",
                  },
                ]}
                selected={editMode}
                onChange={setEditMode}
              />
              <FileUpload setValue={setValues} values={values} />
            </FormLayout>
            <br />
            <InlineStack align="space-between">
              <Button variant="secondary" onClick={() => navigate("/")} id="cancelButton">
                {cms("common.button.cancel")}
              </Button>
              <Button
                variant="primary"
                loading={bulkUploadLoading}
                disabled={bulkUploadLoading || isSubmitDisabled}
                onClick={() => onSubmit()}
              >
                {cms("section.bulk.button.primary")}
              </Button>
            </InlineStack>
            {/* <PageActions
              primaryAction={{
                content: cms("section.bulk.button.primary"),
                id: "submitButton",
                onAction: () => onSubmit(),
                loading: isAnyLoading,
                disabled: isAnyLoading || isSubmitDisabled,
              }}
              secondaryActions={[
                {
                  content: cms("common.button.cancel"),
                  id: "cancelButton",
                  onAction: () => navigate("/"),
                },
              ]}
            /> */}
          </Card.Section>
        </Card>
        {/* deprecated */}
        {/* <Sheet
          isOpen={sheetActive}
          onClose={() => setSheetActive(false)}
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
        /> */}
      </Layout.AnnotatedSection>
    </>
  );
};

export default withErrorBoundary(OperatorProductEditCsv);
